import { CardCheckbox } from '@/components/ui/CardCheckbox'
import { CardRadio } from '@/components/ui/CardRadio'
import { CurrencyInput } from '@/components/ui/CurrencyInput'
import { Field, FieldError, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { type ContractorAnswerField, ContractorQuestionKind } from '@/models/confirmInfo'

/** An answer while it's being edited: text, a dollar amount, or the options picked. */
export type ContractorDraft = string | number | null | string[]

interface ContractorAnswerInputProps {
  id: string
  question: string
  field: ContractorAnswerField
  value: ContractorDraft
  onChange: (value: ContractorDraft) => void
  error?: string
}

/** One of a contractor's answers as a form field, the kind the chat asked it with. */
export function ContractorAnswerInput({
  id,
  question,
  field,
  value,
  onChange,
  error,
}: ContractorAnswerInputProps) {
  return (
    <Field>
      <FieldLabel htmlFor={id} optional={field.optional}>
        {question}
      </FieldLabel>
      {renderControl()}
      <FieldError>{error}</FieldError>
    </Field>
  )

  function renderControl() {
    if (field.kind === ContractorQuestionKind.CHOICE) {
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          {field.options.map((option) => (
            <CardRadio
              key={option.value}
              isSelected={value === option.value}
              onSelect={() => onChange(option.value)}
            >
              {option.label}
            </CardRadio>
          ))}
        </div>
      )
    }
    if (field.kind === ContractorQuestionKind.MULTI) {
      const picked = Array.isArray(value) ? value : []
      return (
        <div className="grid gap-2 sm:grid-cols-2">
          {field.options.map((option) => (
            <CardCheckbox
              key={option.value}
              size="sm"
              isChecked={picked.includes(option.value)}
              onChange={(checked) =>
                onChange(
                  checked ? [...picked, option.value] : picked.filter((picks) => picks !== option.value)
                )
              }
            >
              {option.label}
            </CardCheckbox>
          ))}
        </div>
      )
    }
    if (field.money) {
      return (
        <CurrencyInput
          id={id}
          value={typeof value === 'number' ? value : null}
          onChange={onChange}
          placeholder={field.placeholder}
          aria-invalid={!!error}
        />
      )
    }
    const text = typeof value === 'string' || typeof value === 'number' ? String(value) : ''
    if (field.kind === ContractorQuestionKind.LONG_TEXT) {
      return (
        <Textarea
          id={id}
          value={text}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder}
          maxLength={field.maxLength}
          aria-invalid={!!error}
        />
      )
    }
    return (
      <Input
        id={id}
        value={text}
        onChange={(event) => onChange(event.target.value)}
        placeholder={field.placeholder}
        maxLength={field.maxLength}
        inputMode={field.kind === ContractorQuestionKind.NUMBER ? 'numeric' : undefined}
        aria-invalid={!!error}
      />
    )
  }
}
